<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { createFilledArray } from "@/helpers/utility/Array";
import { formatRoundName } from "../simulationRound/useRoundTime";

import InputDropdown from "@core/input/InputDropdown.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputStateToggle from "@core/input/InputStateToggle.vue";
import ConditionOperatorIcon from "./ConditionOperatorIcon.vue";
import ConditionInputComparedTime from "./ConditionInputComparedTime.vue";

import { useCondition } from "./useCondition";
import { useSettingsStore } from "@editor/store/settings.store";
import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useActionStore } from "../action/actions.store";
import { useIncidentStore } from "../incident/incidents.store";
import {
  getConditionTimeForm,
  getValidComparedProperties,
  getValidComparedTypes
} from "./conditionFunctions";

import type { ConditionComparedType, ConditionItem } from "./ConditionItem";

interface Props {
  conditionId: number;
}
const props = defineProps<Props>();

const targetStore = useTargetStore();
const resourceStore = useResourceStore();
const indexStore = useIndexStore();
const settingsStore = useSettingsStore();
const incidentStore = useIncidentStore();
const actionStore = useActionStore();

const ctx = useCondition(() => props.conditionId);

const { t } = useI18n();

const roundUnit = computed(() => settingsStore.simulationRoundUnit);

const roundUnitName = computed(() => {
  return t(`roundUnits.${roundUnit.value}`);
});

const allAvailableRoundIndices = computed(() => {
  return createFilledArray(settingsStore.simulationRoundAmount).map((i) => ({
    value: i,
    label: formatRoundIndex(i)
  }));
});
const comparedRoundIndex = computed(() => {
  return allAvailableRoundIndices.value.find(
    (i) => i.value == ctx.condition.comparedValue
  );
});

function formatRoundIndex(index: number) {
  return formatRoundName(
    settingsStore.simulationDurationType,
    settingsStore.simulationRoundUnit,
    settingsStore.simulationRoundLength,
    index,
    settingsStore.simulationTimeStart,
    false
  );
}

const unit = computed(() => {
  if (ctx.activatorItem && "unit" in ctx.activatorItem) {
    return ctx.activatorItem.unit;
  }
  return "";
});

const comparedTypes = computed((): ConditionComparedType[] => {
  return getValidComparedTypes(ctx.condition).filter((type) => type != "value");
});
const comparedTypeOptions = computed(() => {
  return comparedTypes.value.map((type) => ({
    value: type,
    label: t(`activatorTypes.${type}`)
  }));
});
const selectedComparedTypeOption = computed(() => {
  return comparedTypeOptions.value.find(
    (option) => option.value == ctx.condition.comparedType
  );
});

const comparedValueTypes = computed(() => {
  if (ctx.condition.activatorType == "round") {
    return ["absoluteRound"];
  }
  if (ctx.condition.activatorProperty == "activationTime") {
    return ["absoluteRound", "relativeRound"];
  }
  return ["value"];
});
const comparedValueTypeOptions = computed(() => {
  return comparedValueTypes.value.map((type) => ({
    value: type,
    label: t(`labels.conditionTimeType.${type}`)
  }));
});
const selectedComparedValueTypeOption = computed(() => {
  return comparedValueTypeOptions.value.find(
    (option) => option.value == ctx.condition.comparedValueType
  );
});

function setComparedType(type: ConditionComparedType) {
  const changes: Partial<ConditionItem> = {
    comparedType: type
  };

  if (type == ctx.condition.comparedType) return;

  changes.comparedId = null;

  ctx.validUpdate(changes);
}

const comparedItems = computed(() => {
  switch (ctx.condition.comparedType) {
    case "target":
      return targetStore.orderedItems;
    case "resource":
      return resourceStore.orderedItems;
    case "index":
      return indexStore.orderedItems;
    case "incident":
      return incidentStore.orderedItems;
    case "action":
      return actionStore.orderedItems;
    default:
      return [];
  }
});

const comparedProperties = computed(() => {
  return getValidComparedProperties(ctx.condition);
});
const comparedPropertyOptions = computed(() => {
  return comparedProperties.value.map((property) => ({
    value: property,
    label: t(`activatorPropertyLabels.${ctx.condition.comparedType}.${property}`, 4)
  }));
});
const selectedComparedPropertyOption = computed(() => {
  return comparedPropertyOptions.value.find(
    (option) => option.value == ctx.condition.comparedProperty
  );
});

function setComparedProperty(property: ConditionItem["comparedProperty"]) {
  const changes: Partial<ConditionItem> = {
    comparedProperty: property
  };

  if (property == ctx.condition.comparedProperty) return;

  const timeForm = getConditionTimeForm(ctx.condition.comparedType, property);

  // Make sure that range properties have good default values set
  if (timeForm == "range") {
    if (ctx.condition.comparedStartType == null) {
      changes.comparedStartType = "startRound";
    }
    if (ctx.condition.comparedEndType == null) {
      changes.comparedEndType = "currentRound";
    }
  }

  ctx.validUpdate(changes);
}
</script>

<template>
  <div class="condition-input-compared">
    <input-state-toggle
      v-slot="{ state }"
      :states="['value', 'object']"
      :value="ctx.condition.comparedType == 'value' ? 'value' : 'object'"
      class="mb-2"
      @update:value="
        ctx.validUpdateKey('comparedType', $event == 'value' ? 'value' : null)
      "
    >
      <template v-if="state == 'value'">
        {{ $t("labels.comparedValue") }}
      </template>
      <template v-if="state == 'object'">
        {{ $t("labels.comparedItem") }}
      </template>
    </input-state-toggle>

    <div v-if="ctx.condition.comparedType == 'value'" class="line">
      <!-- VALUE TYPE INPUT -->
      <input-dropdown
        v-if="comparedValueTypeOptions.length > 1"
        :options="comparedValueTypeOptions"
        :value="selectedComparedValueTypeOption"
        :label="$t('labels.type')"
        class="w-full"
        prop="label"
        item-key="value"
        :searchable="true"
        @update:value="ctx.validUpdateKey('comparedValueType', $event.value)"
      ></input-dropdown>

      <!-- SPECIFIC ROUND INPUT -->
      <input-dropdown
        v-if="ctx.condition.comparedValueType == 'absoluteRound'"
        :options="allAvailableRoundIndices"
        :value="comparedRoundIndex"
        :label="roundUnitName"
        :searchable="true"
        class="w-full"
        prop="label"
        item-key="value"
        @update:value="ctx.validUpdateKey('comparedValue', $event.value)"
      />

      <!-- RELATIVE ROUND INPUT -->
      <input-number
        v-else-if="ctx.condition.comparedValueType == 'relativeRound'"
        :value="ctx.condition.comparedValue"
        :label="$t(`roundUnits.${roundUnit}`, 4)"
        :min="1"
        @update:value="ctx.validUpdateKey('comparedValue', $event)"
      />
      <div v-else-if="ctx.condition.comparedValueType == 'number'">
        <div class="line items-center">
          <condition-operator-icon :operator="ctx.condition.operator" />
          <input-number
            :value="ctx.condition.comparedValue"
            step="any"
            @update:value="ctx.validUpdateKey('comparedValue', $event)"
          />
          <span>{{ unit }}</span>
        </div>
      </div>
    </div>

    <!-- COMPARED ITEM: -->
    <template v-if="ctx.condition.comparedType != 'value'">
      <div class="condition-input-activator column mt-1">
        <input-dropdown
          :label="$t('labels.type')"
          :value="selectedComparedTypeOption"
          :options="comparedTypeOptions"
          :searchable="true"
          item-key="value"
          prop="label"
          @update:value="setComparedType($event.value)"
        ></input-dropdown>

        <input-dropdown
          v-if="ctx.condition.comparedType"
          :label="$t('labels.property')"
          :options="comparedPropertyOptions"
          :value="selectedComparedPropertyOption"
          :searchable="true"
          prop="label"
          item-key="value"
          @update:value="setComparedProperty($event.value)"
        ></input-dropdown>

        <input-dropdown
          v-if="ctx.condition.comparedType && ctx.condition.comparedProperty"
          v-slot="{ item }"
          :label="$t(`activatorTypes.${ctx.condition.comparedType}`)"
          :value="ctx.comparedItem"
          :options="comparedItems"
          :searchable="true"
          @update:value="ctx.validUpdateKey('comparedId', $event.id)"
        >
          {{ item?.title }}
        </input-dropdown>
      </div>
    </template>

    <condition-input-compared-time :condition-id="conditionId" />
  </div>
</template>
